/**
 * SMS Sender Pool Page
 *
 * Manage the shared pool of outbound SMS sender phone numbers.
 * Outbound SMS picks from this pool with per-Contact stickiness so the same
 * contact always gets the same sender (and replies route consistently).
 */
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from '@tanstack/react-router';
import { Phone, ArrowLeft, Plus, Trash2, AlertCircle } from 'lucide-react';
import { useSmsSendersStore } from '../../stores/useSmsSendersStore';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const E164_REGEX = /^\+[1-9]\d{7,14}$/;

export function SmsSenders() {
  const {
    senders,
    isLoading,
    isMutating,
    error,
    loadSenders,
    addSender,
    updateSender,
    removeSender,
    clearError,
  } = useSmsSendersStore();

  const [showAdd, setShowAdd] = useState(false);
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    loadSenders();
  }, [loadSenders]);

  const activeCount = useMemo(() => senders.filter((s) => s.isActive).length, [senders]);

  const onAdd = async () => {
    setValidationError(null);
    if (!E164_REGEX.test(newPhoneNumber.trim())) {
      setValidationError('Phone number must be E.164 format (e.g. +18338612844)');
      return;
    }
    const ok = await addSender(newPhoneNumber.trim(), newLabel.trim() || null);
    if (ok) {
      setShowAdd(false);
      setNewPhoneNumber('');
      setNewLabel('');
    }
  };

  const onDelete = async (id: string, phoneNumber: string) => {
    if (!window.confirm(`Remove ${phoneNumber} from the sender pool?`)) return;
    await removeSender(id);
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="flex flex-col h-full"
    >
      {/* Header */}
      <motion.div
        variants={itemVariants}
        className="flex-shrink-0 px-6 py-6 border-b border-slate-200 dark:border-slate-700"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/settings"
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-500" />
            </Link>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-200 dark:from-emerald-900/40 dark:to-emerald-800/40 flex items-center justify-center">
              <Phone className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                SMS Sender Numbers
              </h1>
              <p className="text-slate-500 dark:text-slate-400">
                Shared pool of outbound phone numbers — distributed evenly with per-contact stickiness
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 text-sm text-slate-700 dark:text-slate-300">
            <span className="font-medium">{activeCount}</span>
            <span className="text-slate-500">active / {senders.length} total</span>
          </div>
        </div>
      </motion.div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <motion.div variants={itemVariants} className="max-w-4xl space-y-4">
          {/* Add button / form */}
          {!showAdd ? (
            <button
              onClick={() => setShowAdd(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              Add Sender Number
            </button>
          ) : (
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 space-y-3">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                Add a phone number to the pool
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Phone number (E.164)
                  </label>
                  <input
                    type="text"
                    placeholder="+18338612844"
                    value={newPhoneNumber}
                    onChange={(e) => setNewPhoneNumber(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Label (optional)
                  </label>
                  <input
                    type="text"
                    placeholder="Pool 3"
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>
              {validationError && (
                <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                  <AlertCircle className="w-4 h-4" />
                  {validationError}
                </div>
              )}
              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={onAdd}
                  disabled={isMutating || !newPhoneNumber.trim()}
                  className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isMutating ? 'Adding...' : 'Add'}
                </button>
                <button
                  onClick={() => {
                    setShowAdd(false);
                    setNewPhoneNumber('');
                    setNewLabel('');
                    setValidationError(null);
                  }}
                  className="px-4 py-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Error banner */}
          {error && (
            <div className="rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 text-sm text-red-700 dark:text-red-300">{error}</div>
              <button onClick={clearError} className="text-xs text-red-600 dark:text-red-400 hover:underline">
                Dismiss
              </button>
            </div>
          )}

          {/* Sender list */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
            {isLoading && senders.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500">Loading...</div>
            ) : senders.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500">
                No sender numbers yet. Add one to start sending SMS.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800/50">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-slate-500 dark:text-slate-400">Phone</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-500 dark:text-slate-400">Label</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-500 dark:text-slate-400">Status</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-500 dark:text-slate-400">Added</th>
                    <th className="text-right px-4 py-3 font-medium text-slate-500 dark:text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {senders.map((s) => (
                    <tr key={s.id}>
                      <td className="px-4 py-3 font-mono text-slate-900 dark:text-white">{s.phoneNumber}</td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{s.label || '—'}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => updateSender(s.id, { isActive: !s.isActive })}
                          disabled={isMutating}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors disabled:opacity-50 ${
                            s.isActive
                              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400 hover:bg-emerald-200'
                              : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 hover:bg-slate-200'
                          }`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${s.isActive ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                          {s.isActive ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-slate-500 dark:text-slate-400">
                        {new Date(s.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => onDelete(s.id, s.phoneNumber)}
                          disabled={isMutating}
                          className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Help text */}
          <div className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
            Each contact is permanently bound to one sender via a deterministic hash, so replies always
            come back to the same number. Distribution is statistically even at scale. Inactive numbers
            are skipped, and contacts on inactive numbers automatically rebalance to the next available
            sender.
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
